import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Target, Flame, TrendingUp, LogOut, User, Settings, Bell, Crown, HelpCircle, Sparkles, BarChart2 } from 'lucide-react';
import KoruLogo from './KoruLogo';
import { HabitSuggestion } from '../services/aiSuggestionApi';
import { useHabitStore } from '../store/habitStore';
import { useAuthStore } from '../store/authStore';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { HabitCard } from './habits/HabitCard';
import { HabitDetails } from './habits/HabitDetails';
import CreateHabitForm from './habits/CreateHabitForm';
import EditHabitForm from './habits/EditHabitForm';
import { UserProfile } from './UserProfile';
import { NotificationPreferences, NotificationHistory } from './notifications';
// import { NotificationTester } from './notifications'; // Temporarily disabled
// import { NotificationTester } from './notifications/NotificationTester'; // Temporarily disabled
import { PricingModal } from './subscription/PricingModal';
import { HelpSupportModal } from './support';
import { WeeklyInsightsPanel } from './insights/WeeklyInsightsPanel';
import { WeeklyInsightsBanner } from './insights/WeeklyInsightsBanner';
import { useInsightsStore } from '../store/insightsStore';
import { ThemeToggle } from './ui/ThemeToggle';
import Modal from './ui/Modal';
import { Button } from './ui/Button';
import { Habit, HabitFrequency } from '../types';
import { habitApiService, HabitResponse, DashboardStatsResponse, ToggleHabitCompletionRequest } from '../services/habitApi';
import { getDisplayAvatarUrl, getProxyAvatarUrl } from '../utils/avatarUtils';
import toast from 'react-hot-toast';
import { TimezoneSetupModal } from './ui/TimezoneSetupModal';
import { getTodayDateStringInTimezone, getUserTimezoneFromLocal } from '../utils/timezone';

type NotificationView = 'preferences' | 'history';

interface DashboardProps {
  onNavigateToDiscover?: () => void;
  pendingDiscoverySuggestion?: HabitSuggestion | null;
  onDiscoverySuggestionConsumed?: (saved: boolean) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  onNavigateToDiscover,
  pendingDiscoverySuggestion,
  onDiscoverySuggestionConsumed,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showDiscoverBanner, setShowDiscoverBanner] = useState(
    () => localStorage.getItem('koru_discover_banner_dismissed') !== 'true'
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isHelpSupportModalOpen, setIsHelpSupportModalOpen] = useState(false);
  const [isInsightsPanelOpen, setIsInsightsPanelOpen] = useState(false);
  const [showInsightsBanner, setShowInsightsBanner] = useState(false);
  const [notificationView, setNotificationView] = useState<NotificationView>('preferences');
  const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
  const [selectedHabit, setSelectedHabit] = useState<Habit | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStatsResponse | null>(null);
  const [showTimezonePrompt, setShowTimezonePrompt] = useState(false);
  
    const {
    habits: storeHabits,
    setHabits,
    setLoading,
    setError,
    deleteHabit,
    updateHabit,
    getTodaysCompletions,
    isLoading,
    error
  } = useHabitStore();

  const { user, logout, isAuthenticated, token, isTokenExpired, hasDismissedTimezonePrompt, dismissTimezonePrompt, completeFirstLoginSetup, resetTimezonePrompt } = useAuthStore();
  const {
    currentTier,
    isInTrial,
    isInGracePeriod,
    isTrialExpired,
    getStatusText,
    getTierDisplayName,
    canAccessWeeklyInsights,
  } = useFeatureAccess();

  const { shouldShowInsightsPopup, markInsightsPopupViewed } = useInsightsStore();

  // Use store state directly to ensure reactivity
  const activeHabits = storeHabits.filter(habit => habit.isActive);
  const todaysCompletions = getTodaysCompletions();

  // Separate useEffect to handle modal closing when setup is completed
  useEffect(() => {
    if (user && user.hasCompletedFirstLoginSetup && showTimezonePrompt) {
      setShowTimezonePrompt(false);
    }
  }, [user, showTimezonePrompt]);

  useEffect(() => {
    if (user && isAuthenticated) {
      // Only show timezone setup on first login if user hasn't completed first login setup
      const isFirstLogin = !user.hasCompletedFirstLoginSetup;

      // Show timezone setup for first login users (both regular and Google sign-in)
      if (isFirstLogin && !showTimezonePrompt) {
        // Reset the dismissed prompt for first login users
        if (hasDismissedTimezonePrompt) {
          resetTimezonePrompt();
        }
        setShowTimezonePrompt(true);
      }
    }
  }, [user, isAuthenticated, hasDismissedTimezonePrompt, resetTimezonePrompt, showTimezonePrompt]);

  const handleConfirmTimezone = async (tz: string) => {
    try {
      const ok = await completeFirstLoginSetup(tz);
      if (ok) {
        dismissTimezonePrompt();
        setShowTimezonePrompt(false);
      }
    } catch (e) {
      console.error('Failed to save timezone:', e);
      toast.error('Failed to save timezone');
    }
  };

  const handleCloseTimezonePrompt = () => {
    dismissTimezonePrompt();
    setShowTimezonePrompt(false);
  };
  
  // Use dashboard stats from API if available, otherwise calculate from local data
  const totalActiveStreaks = dashboardStats?.activeStreaks ?? activeHabits.filter(habit => habit.currentStreak > 0).length;
  const completedToday = dashboardStats?.completedToday ?? todaysCompletions.length;
  const longestStreak = dashboardStats?.longestStreak ?? Math.max(...activeHabits.map(habit => habit.longestStreak), 0);
  const totalHabits = dashboardStats?.totalHabits ?? activeHabits.length;

  // Convert HabitResponse to Habit format for the store
  const convertApiResponseToHabit = (response: HabitResponse): Habit => {
    // Map numeric frequency back to string enum for internal use
    const frequencyMap: Record<number, HabitFrequency> = {
      0: HabitFrequency.Daily,
      1: HabitFrequency.WeeklySpecificDays,
      2: HabitFrequency.WeeklyCount,
      3: HabitFrequency.IntervalBased
    };

    return {
      id: response.id,
      name: response.name,
      description: response.description || '',
      iconName: response.iconName,
      colorCode: response.colorCode,
      frequency: frequencyMap[response.frequency] || HabitFrequency.Daily,
      targetStreakLength: response.targetStreakLength,
      isActive: response.isActive,
      createdAt: response.createdAt,
      currentStreak: response.currentStreak,
      longestStreak: response.longestStreak,
      completionRate: response.completionRate,
      isCompletedToday: response.isCompletedToday,
      // Include frequency-specific properties
      weeklyDays: response.weeklyDays,
      weeklyCount: response.weeklyCount,
      intervalDays: response.intervalDays
    };
  };

  // Fetch habits from API
  const fetchHabits = useCallback(async () => {
    // Don't make API calls if not properly authenticated
    if (!isAuthenticated || !token || isTokenExpired()) {
      console.warn('Skipping fetchHabits - not authenticated or token expired');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await habitApiService.getHabits(false); // Only active habits
      const habits = response.map(convertApiResponseToHabit);
      setHabits(habits);
    } catch (error) {
      console.error('Failed to fetch habits:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch habits');
      toast.error('Failed to load habits');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setHabits, isAuthenticated, token, isTokenExpired]);

  // Fetch dashboard stats from API
  const fetchDashboardStats = useCallback(async () => {
    // Don't make API calls if not properly authenticated
    if (!isAuthenticated || !token || isTokenExpired()) {
      console.warn('Skipping fetchDashboardStats - not authenticated or token expired');
      return;
    }

    try {
      const stats = await habitApiService.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      // Don't show error toast for stats, as we have fallback calculations
    }
  }, [isAuthenticated, token, isTokenExpired]);

  const { loadFeatureAccess, loadSubscription } = useSubscriptionStore();

  // Load habits, dashboard stats, and subscription data when component mounts
  useEffect(() => {
    if (user && isAuthenticated && token && !isTokenExpired()) {
      fetchHabits();
      fetchDashboardStats();
      loadFeatureAccess();
      loadSubscription();
    }
  }, [user, isAuthenticated, token, isTokenExpired, fetchHabits, fetchDashboardStats, loadFeatureAccess, loadSubscription]);

  useEffect(() => {
    if (canAccessWeeklyInsights && user?.createdAt && shouldShowInsightsPopup(user.createdAt)) {
      setShowInsightsBanner(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessWeeklyInsights, user?.createdAt]);

  useEffect(() => {
    if (pendingDiscoverySuggestion) {
      setIsCreateModalOpen(true);
    }
  }, [pendingDiscoverySuggestion]);

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    onDiscoverySuggestionConsumed?.(false);
  };

  const handleHabitCreated = () => {
    setIsCreateModalOpen(false);
    onDiscoverySuggestionConsumed?.(true);
    // Refresh habits list from API
    fetchHabits();
    fetchDashboardStats();
  };

  const handleEditHabit = (habit: Habit) => {
    setEditingHabit(habit);
    setIsEditModalOpen(true);
  };

  const handleHabitClick = (habit: Habit) => {
    setSelectedHabit(habit);
  };

  const handleBackFromDetails = () => {
    setSelectedHabit(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingHabit(null);
  };

  const handleHabitUpdated = async () => {
    setIsEditModalOpen(false);
    setEditingHabit(null);

    // Refresh habits list and stats from API
    fetchHabits();
    fetchDashboardStats();

    // If we're viewing details of the updated habit, fetch the updated habit data directly
    if (selectedHabit && editingHabit?.id === selectedHabit.id) {
      try {
        const updatedHabitResponse = await habitApiService.getHabit(selectedHabit.id);
        const updatedHabit = convertApiResponseToHabit(updatedHabitResponse);
        setSelectedHabit(updatedHabit);
      } catch (error) {
        console.warn('Failed to fetch updated habit data for details view:', error);
        // Fallback: try to find from store (might still be old data)
        const updatedHabit = storeHabits.find(h => h.id === selectedHabit.id);
        if (updatedHabit) {
          setSelectedHabit(updatedHabit);
        }
      }
    }
  };

  const handleDeleteHabit = async (habitId: string) => {
    try {
      setLoading(true);
      const deleteResponse = await habitApiService.deleteHabit(habitId);
      
      if (deleteResponse.success) {
        // Update the store to remove the habit and its completions
        deleteHabit(habitId);
        
        // Update dashboard stats with the response from the API
        setDashboardStats(deleteResponse.updatedStats);
        
        // If we're viewing details of the deleted habit, go back to dashboard
        if (selectedHabit?.id === habitId) {
          setSelectedHabit(null);
        }
        
        toast.success(deleteResponse.message);
      } else {
        toast.error(deleteResponse.message);
      }
    } catch (error) {
      console.error('Failed to delete habit:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete habit');
      // If API call failed, refresh from server to ensure consistency
      fetchHabits();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleHabitCompletion = async (habitId: string, date?: string) => {
    const userTimezone = getUserTimezoneFromLocal();
    const targetDate = date || getTodayDateStringInTimezone(userTimezone);

    // Get current habit state for optimistic update and potential rollback
    const currentHabit = storeHabits.find(h => h.id === habitId);
    if (!currentHabit) return;

    // Store original state for potential rollback
    const originalHabit = { ...currentHabit };
    const originalSelectedHabit = selectedHabit?.id === habitId ? { ...selectedHabit } : null;

    // OPTIMISTIC UPDATE: Update UI immediately before API call
    const optimisticIsCompleted = !currentHabit.isCompletedToday;
    const optimisticStreak = optimisticIsCompleted
      ? currentHabit.currentStreak + 1
      : Math.max(0, currentHabit.currentStreak - 1);

    updateHabit(habitId, {
      isCompletedToday: optimisticIsCompleted,
      currentStreak: optimisticStreak
    });

    // Update selected habit if in details view
    if (selectedHabit?.id === habitId) {
      setSelectedHabit({
        ...selectedHabit,
        isCompletedToday: optimisticIsCompleted,
        currentStreak: optimisticStreak
      });
    }

    try {
      const request: ToggleHabitCompletionRequest = {
        habitId,
        completedDate: targetDate
      };

      const response = await habitApiService.toggleHabitCompletion(request);

      if (response.success) {
        // Use the actual stats from the API response (now includes streak data)
        updateHabit(habitId, {
          isCompletedToday: response.isCompleted,
          currentStreak: response.currentStreak ?? optimisticStreak,
          longestStreak: response.longestStreak ?? currentHabit.longestStreak,
          completionRate: response.completionRate ?? currentHabit.completionRate
        });

        // Update dashboard stats with the response from the API
        setDashboardStats(response.updatedStats);

        // Update selected habit if in details view
        if (selectedHabit?.id === habitId) {
          setSelectedHabit({
            ...selectedHabit,
            isCompletedToday: response.isCompleted,
            currentStreak: response.currentStreak ?? optimisticStreak,
            longestStreak: response.longestStreak ?? currentHabit.longestStreak,
            completionRate: response.completionRate ?? currentHabit.completionRate
          });
        }

        // Optional: Show success message
        // toast.success(response.message);
      } else {
        // API returned success: false - revert optimistic update
        updateHabit(habitId, originalHabit);
        if (originalSelectedHabit) {
          setSelectedHabit(originalSelectedHabit);
        }
        toast.error(response.message);
      }
    } catch (error) {
      // API call failed - revert optimistic update
      console.error('Failed to toggle habit completion:', error);
      updateHabit(habitId, originalHabit);
      if (originalSelectedHabit) {
        setSelectedHabit(originalSelectedHabit);
      }
      toast.error('Failed to update habit completion');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  const handleProfileUpdated = () => {
    setIsProfileModalOpen(false);
    // The auth store will automatically update the user data
  };

  const handleOpenNotificationModal = (view: NotificationView = 'preferences') => {
    setNotificationView(view);
    setIsNotificationModalOpen(true);
  };

  const handleCloseNotificationModal = () => {
    setIsNotificationModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg overflow-hidden">
                <KoruLogo size={28} className="sm:hidden w-full h-full" />
                <KoruLogo size={32} className="hidden sm:block w-full h-full" />
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">Koru</h1>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Discover Habits */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateToDiscover}
                  className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  title="Discover Habits"
                >
                  <Sparkles size={18} />
                </Button>
              </div>

              {/* Weekly Insights */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsInsightsPanelOpen(true)}
                  className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                  title="Weekly Insights"
                >
                  <BarChart2 size={18} />
                </Button>
              </div>

              {/* Notifications */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenNotificationModal('preferences')}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  title="Notification Settings"
                >
                  <Bell size={18} />
                </Button>
              </div>

              {/* Help & Support */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsHelpSupportModalOpen(true)}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  title="Help & Support"
                >
                  <HelpCircle size={18} />
                </Button>
              </div>
              
              {/* User Info */}
              {user && (
                <div className="flex items-center space-x-1 sm:space-x-3">
                  <button
                    onClick={handleOpenProfileModal}
                    className="flex items-center space-x-1 sm:space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-1 sm:p-2 transition-colors group"
                    title="Edit Profile"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                      {user.avatar ? (
                        <img 
                          src={getDisplayAvatarUrl(user, 32, false)} 
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Try proxy URL as fallback for Google images
                            const proxyUrl = getProxyAvatarUrl(user);
                            if (proxyUrl && e.currentTarget.src !== proxyUrl) {
                              e.currentTarget.src = proxyUrl;
                              return;
                            }
                            // Final fallback to User icon if proxy also fails
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <User className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${user.avatar ? 'hidden' : ''}`} />
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
                          {user.firstName} {user.lastName}
                        </p>
                        {(isInTrial || isInGracePeriod || isTrialExpired || currentTier > 0) && (
                          <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${
                            isInTrial 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : isTrialExpired
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : isInGracePeriod
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                              : currentTier === 2
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {isInTrial ? 'Trial' : isTrialExpired ? 'Expired' : isInGracePeriod ? 'Grace' : getTierDisplayName()}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                    <Settings className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
                  </button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    leftIcon={<LogOut size={14} />}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 px-2 sm:px-3"
                  >
                    <span className="hidden sm:inline text-sm">Logout</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Status Banner */}
      {(isInTrial || isInGracePeriod || isTrialExpired) && (
        <div className={`border-b ${
          isInTrial 
            ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
            : isTrialExpired
            ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            : 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
        }`}>
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-2 sm:py-3">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <Crown className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${
                  isInTrial 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : isTrialExpired
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-orange-600 dark:text-orange-400'
                }`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs sm:text-sm font-medium truncate ${
                    isInTrial 
                      ? 'text-blue-800 dark:text-blue-200' 
                      : isTrialExpired
                      ? 'text-red-800 dark:text-red-200'
                      : 'text-orange-800 dark:text-orange-200'
                  }`}>
                    {getStatusText()}
                  </p>
                  <p className={`text-xs hidden sm:block ${
                    isInTrial 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : isTrialExpired
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-orange-600 dark:text-orange-400'
                  }`}>
                    {isInTrial 
                      ? 'Choose a plan to continue after your trial'
                      : isTrialExpired
                      ? 'Subscribe now to regain access to all features'
                      : 'Subscribe now to keep your data and continue using Koru'
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setIsPricingModalOpen(true)}
                variant="primary"
                size="sm"
                className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
              >
                <span className="hidden sm:inline">{isInTrial ? 'Choose Plan' : 'Subscribe Now'}</span>
                <span className="sm:hidden">{isInTrial ? 'Plan' : 'Subscribe'}</span>
              </Button>
            </div>
          </div>
        </div>
      )}



      {/* Main Content */}
      {selectedHabit ? (
        <HabitDetails
          habit={selectedHabit}
          onBack={handleBackFromDetails}
          onEdit={handleEditHabit}
          onDelete={handleDeleteHabit}
          onToggleCompletion={handleToggleHabitCompletion}
        />
      ) : (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Welcome Message */}
          {user && (
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                Welcome back, {user.firstName}! 👋
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                Let's continue building your habits and maintaining your streaks.
              </p>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
            <div className="card p-3 sm:p-4 lg:p-6">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-2 sm:ml-3 lg:ml-4 flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Total Habits</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">{totalHabits}</p>
                  
                  {/* Habit Limit Progress for Free Users */}
                  {currentTier === 0 && (
                    <div className="mt-1 sm:mt-2">
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span className="truncate">Free Plan</span>
                        <span className="text-xs">{totalHabits}/3</span>
                      </div>
                      <div className="w-full h-1 sm:h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${
                            totalHabits >= 3 
                              ? 'bg-red-500' 
                              : totalHabits >= 2 
                              ? 'bg-orange-500' 
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min((totalHabits / 3) * 100, 100)}%` }}
                        />
                      </div>
                      {totalHabits >= 3 && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 truncate">
                          Limit reached
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-4 lg:p-6">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <Flame className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Active Streaks</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">{totalActiveStreaks}</p>
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-4 lg:p-6">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Completed Today</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">{completedToday}</p>
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-4 lg:p-6">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Flame className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Longest Streak</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">{longestStreak}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Insights Banner */}
          {showInsightsBanner && (
            <WeeklyInsightsBanner
              onView={() => {
                markInsightsPopupViewed();
                setShowInsightsBanner(false);
                setIsInsightsPanelOpen(true);
              }}
              onDismiss={() => {
                markInsightsPopupViewed();
                setShowInsightsBanner(false);
              }}
            />
          )}

          {/* Habits Section */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">Your Habits</h2>
              <Button
                onClick={handleOpenCreateModal}
                variant="outline"
                leftIcon={<Plus size={14} />}
                className="flex-shrink-0 text-sm px-3 py-2"
              >
                <span className="hidden sm:inline">Add Habit</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>

            {isLoading ? (
              <div className="card p-6 sm:p-8 lg:p-12 text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                  <Target className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-gray-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">Loading habits...</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Please wait while we fetch your habits.</p>
              </div>
            ) : error ? (
              <div className="card p-6 sm:p-8 lg:p-12 text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Target className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-red-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">Failed to load habits</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">{error}</p>
                <Button
                  onClick={fetchHabits}
                  leftIcon={<Plus size={14} />}
                  variant="outline"
                  size="sm"
                >
                  Try Again
                </Button>
              </div>
            ) : activeHabits.length === 0 ? (
              <div>
                {showDiscoverBanner && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-indigo-900 dark:text-indigo-200 text-sm">Not sure where to start?</p>
                      <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">Answer 3 quick questions and get 6 personalised habit suggestions.</p>
                      <button
                        onClick={onNavigateToDiscover}
                        className="mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Discover habits →
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        localStorage.setItem('koru_discover_banner_dismissed', 'true');
                        setShowDiscoverBanner(false);
                      }}
                      className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 text-lg leading-none flex-shrink-0"
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}
                <div className="card p-6 sm:p-8 lg:p-12 text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Target className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">No habits yet</h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
                    Start building positive habits by adding your first one.
                  </p>
                  <Button
                    onClick={handleOpenCreateModal}
                    leftIcon={<Plus size={14} />}
                    size="sm"
                  >
                    Create Your First Habit
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                {activeHabits.map((habit) => (
                  <HabitCard
                    key={habit.id}
                    habit={habit}
                    onEdit={handleEditHabit}
                    onDelete={handleDeleteHabit}
                    onToggleCompletion={handleToggleHabitCompletion}
                    onClick={handleHabitClick}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchHabits}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Refresh Data'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Habit Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title="Create New Habit"
        size="lg"
      >
                        <CreateHabitForm
                  onSuccess={handleHabitCreated}
                  onUpgrade={() => setIsPricingModalOpen(true)}
                  initialSuggestion={pendingDiscoverySuggestion ?? undefined}
                />
      </Modal>

      {/* Edit Habit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Habit"
        size="lg"
      >
        {editingHabit && (
          <EditHabitForm
            habit={editingHabit}
            onSuccess={handleHabitUpdated}
            onCancel={handleCloseEditModal}
          />
        )}
      </Modal>

      {/* Profile Modal */}
      <Modal
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        title="Profile Settings"
        size="md"
      >
        <UserProfile onSuccess={handleProfileUpdated} />
      </Modal>

      {/* Notification Modal */}
      <Modal
        isOpen={isNotificationModalOpen}
        onClose={handleCloseNotificationModal}
        title="Notification Settings"
        size="lg"
      >
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setNotificationView('preferences')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                notificationView === 'preferences'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Preferences
            </button>
            <button
              onClick={() => setNotificationView('history')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                notificationView === 'history'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              History
            </button>
            {/* Test tab temporarily disabled
            <button
              onClick={() => setNotificationView('tester')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                notificationView === 'tester'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Test
            </button>
            */}
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {notificationView === 'preferences' ? (
              <NotificationPreferences />
            ) : notificationView === 'history' ? (
              <NotificationHistory />
            ) : (
              // Test tab temporarily disabled
              // <NotificationTester />
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Test tab is temporarily disabled</p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />

      {/* Help & Support Modal */}
      <HelpSupportModal
        isOpen={isHelpSupportModalOpen}
        onClose={() => setIsHelpSupportModalOpen(false)}
      />

      {/* Weekly Insights Modal */}
      <Modal
        isOpen={isInsightsPanelOpen}
        onClose={() => setIsInsightsPanelOpen(false)}
        title="Weekly Insights"
        size="lg"
      >
        <WeeklyInsightsPanel
          onUpgrade={() => {
            setIsInsightsPanelOpen(false);
            setIsPricingModalOpen(true);
          }}
        />
      </Modal>

      {/* Timezone Setup Modal */}
      <TimezoneSetupModal
        isOpen={showTimezonePrompt}
        onClose={handleCloseTimezonePrompt}
        onConfirm={handleConfirmTimezone}
        isFirstLogin={!user?.hasCompletedFirstLoginSetup}
      />
    </div>
  );
}; 